// Apolo IDE — useTheme hook + ThemeContext (Sprint v10 light mode).
//
// Context lives here (not in ThemeProvider.jsx) so the component file only
// exports a React component — required by react-refresh/only-export-components
// for HMR.
//
// Usage:
//   const { theme, resolved, setTheme, toggle } = useTheme();
//   - theme: user preference (persisted in localStorage)
//   - resolved: what the DOM is actually showing right now
//   - setTheme('light' | 'dark' | 'system')
//   - toggle(): flips light ↔ dark (system → opposite of current resolved)

import { createContext, useContext } from 'react';
import { DEFAULT_ACCENT } from '../lib/accentTheme.js';

export const ThemeContext = createContext(null);

export const THEME_STORAGE_KEY = 'apolo:theme';
export const THEMES = Object.freeze({
  LIGHT: 'light',
  DARK: 'dark',
  SYSTEM: 'system',
});

// Safe default for components rendered outside <ThemeProvider> (e.g.
// individual unit tests). Production main.jsx always wraps in
// ThemeProvider, so this only triggers in tests or screenshot pipelines
// that mount one component in isolation.
const FALLBACK = Object.freeze({
  theme: THEMES.DARK,
  resolved: THEMES.DARK,
  setTheme: () => {},
  toggle: () => {},
  // design-uplift E2 — accent tint (hue + intensity).
  accent: DEFAULT_ACCENT,
  setAccent: () => {},
  resetAccent: () => {},
});

export function useTheme() {
  const ctx = useContext(ThemeContext);
  return ctx ?? FALLBACK;
}
